// OAuth service using direct Supabase OAuth (frontend-initiated)
// This ensures the frontend Supabase client gets the session properly
import { getSupabaseClient } from './supabaseClient';

export interface OAuthUser {
  id: string
  email: string
  name?: string
  avatar_url?: string
  provider: 'google' | 'apple' | 'microsoft'
}

export interface OAuthResponse {
  user: OAuthUser | null
  session: any | null
  error: Error | null
}

class OAuthService {
  /**
   * Sign in with Google OAuth - uses direct Supabase OAuth
   * Supabase will redirect to /auth/callback after successful OAuth
   */
  async signInWithGoogle(): Promise<OAuthResponse> {
    try {
      const supabase = await getSupabaseClient();
      const redirectUrl = `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        console.error('[OAuth] Google OAuth error:', error);
        return { user: null, session: null, error };
      }

      // Supabase will handle the redirect
      return { user: null, session: null, error: null };
    } catch (error) {
      console.error('[OAuth] Google OAuth exception:', error);
      return {
        user: null,
        session: null,
        error: error instanceof Error ? error : new Error('OAuth sign-in failed'),
      };
    }
  }

  /**
   * Sign in with Apple OAuth - uses direct Supabase OAuth
   */
  async signInWithApple(): Promise<OAuthResponse> {
    try {
      const supabase = await getSupabaseClient();
      const redirectUrl = `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) {
        console.error('[OAuth] Apple OAuth error:', error);
        return { user: null, session: null, error };
      }

      return { user: null, session: null, error: null };
    } catch (error) {
      console.error('[OAuth] Apple OAuth exception:', error);
      return {
        user: null,
        session: null,
        error: error instanceof Error ? error : new Error('OAuth sign-in failed'),
      };
    }
  }

  /**
   * Sign in with Microsoft OAuth - uses direct Supabase OAuth (azure provider)
   */
  async signInWithMicrosoft(): Promise<OAuthResponse> {
    try {
      const supabase = await getSupabaseClient();
      const redirectUrl = `${window.location.origin}/auth/callback`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo: redirectUrl,
          scopes: 'email profile openid',
        },
      });

      if (error) {
        console.error('[OAuth] Microsoft OAuth error:', error);
        return { user: null, session: null, error };
      }

      return { user: null, session: null, error: null };
    } catch (error) {
      console.error('[OAuth] Microsoft OAuth exception:', error);
      return {
        user: null,
        session: null,
        error: error instanceof Error ? error : new Error('OAuth sign-in failed'),
      };
    }
  }
}

// Export singleton instance
const oauthService = new OAuthService()
export { oauthService }
export default oauthService
